import { startOfDay } from '@if/core/date'
import ClearIcon from '@mui/icons-material/Clear'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material'
import { getProgramsByCompanyId } from '@src/data/types/FinancingCompanies'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { debounce } from 'lodash-es'
import { ChangeEvent, memo, SetStateAction, useEffect, useMemo, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { InputTextField, MultipleSelectCheckmarks, SelectComponent } from '../../components'
import {
  useAllPlansForPrograms,
  useCurrentLang,
  useFinancingProgramEnum,
  useMerchantTagKeys,
  useMerchantTags,
  useTerritories,
} from '../../data/selectors/app-config-selectors'

import {
  ECreditApplicationStatus,
  EFinancingCompany,
  EFinancingProgram,
  EIFinanceTeam,
  EProgressionStatus,
  ESourceSystem,
  getELoanPurposeList,
  getEOriginSystemIdList,
  getEProgressionStatusList,
} from '@if/core/types'
import { formatUtcIsoDateTime } from '@if/core/utils'
import { NumberField } from '@if/mui'
import { useGetMerchantById, useSearchMerchants } from '@src/data/api/merchant/merchant-api'
import { Merchant, SelectValueListItem } from '../../data/types'
import { CreditDashboardFilters } from '../../data/types/CreditDashboardEntry'

interface Props {
  filterValues: CreditDashboardFilters
  resetFilter: () => void
  applyFilter: (data: CreditDashboardFilters) => void
  addDays: (date: Date, days: number) => Date
  onCancel: () => void
  open: boolean
}

const SearchFilter = ({
  filterValues,
  resetFilter,
  applyFilter,
  addDays,
  onCancel,
  open,
}: Props): JSX.Element => {
  const { t } = useTranslation()
  const { selectedCompany } = usePreferences()
  const territories = useTerritories()

  const lang = useCurrentLang()
  const financingProgramEnum = useFinancingProgramEnum()
  const programsByCompanyId = getProgramsByCompanyId(selectedCompany ? [selectedCompany] : [])

  // Helper function to get active programs for a financing program (inline logic to avoid hook in useMemo)
  const getActivePrograms = (programId: EFinancingProgram): EFinancingProgram[] => {
    switch (programId) {
      case EFinancingProgram.Auto:
        return []
      case EFinancingProgram.Personal:
        return [EFinancingProgram.Personal]
      case EFinancingProgram.B2c:
        return [EFinancingProgram.B2c]
      case EFinancingProgram.IfXpress:
        return [EFinancingProgram.IfXpress]
      case EFinancingProgram.Products:
        return [EFinancingProgram.Products]
      case EFinancingProgram.AutoRsla:
        return [EFinancingProgram.AutoRsla]
      case EFinancingProgram.CreditRsla:
        return [EFinancingProgram.CreditRsla]
      case EFinancingProgram.MechanicalWarranty:
        return [EFinancingProgram.MechanicalWarranty]
      default:
        return []
    }
  }

  const activeProgramsList = useMemo(
    () => programsByCompanyId.flatMap((programId) => getActivePrograms(programId)),
    [programsByCompanyId],
  )

  const tags = useMerchantTags()
  const tagKeys = useMerchantTagKeys()

  const activePrograms = useMemo(
    () => ({
      activePrograms: activeProgramsList,
    }),
    [activeProgramsList],
  )

  const filteredFinancingPrograms = useMemo(
    () =>
      Object.values(financingProgramEnum).filter((program) =>
        activePrograms.activePrograms.includes(program.id as EFinancingProgram),
      ),
    [financingProgramEnum, activePrograms],
  )

  const filteredFinancingProgramIds = useMemo(
    () => filteredFinancingPrograms.map((program) => program.id as EFinancingProgram),
    [filteredFinancingPrograms],
  )

  const allPlans = useAllPlansForPrograms(filteredFinancingProgramIds)

  const programPlans = useMemo(() => {
    return allPlans.map((plan) => ({
      label: plan[lang],
      value: plan.id,
    }))
  }, [allPlans, lang])

  const iFinanceTeams: SelectValueListItem[] = useMemo(
    () => [
      {
        label: t('common.all'),
        value: '',
      },
      {
        label: 'iFinance',
        value: EIFinanceTeam.IFinance,
      },
      {
        label: 'Medicard',
        value: EIFinanceTeam.Medicard,
      },
    ],
    [t],
  )

  const creditApplicationStatuses: SelectValueListItem[] = useMemo(
    () => [
      {
        label: t('common.all'),
        value: '',
      },
      {
        label: t('ECreditApplicationStatus.active', { ns: 'enum' }),
        value: ECreditApplicationStatus.Active,
      },
      {
        label: t('ECreditApplicationStatus.cancelled', { ns: 'enum' }),
        value: ECreditApplicationStatus.Cancelled,
      },
      {
        label: t('ECreditApplicationStatus.completed', { ns: 'enum' }),
        value: ECreditApplicationStatus.Completed,
      },
      {
        label: t('ECreditApplicationStatus.draft', { ns: 'enum' }),
        value: ECreditApplicationStatus.Draft,
      },
      {
        label: t('ECreditApplicationStatus.expired', { ns: 'enum' }),
        value: ECreditApplicationStatus.Expired,
      },
    ],
    [t],
  )

  const dateRanges: SelectValueListItem[] = useMemo(() => {
    const now = new Date()
    const tomorrow = startOfDay(addDays(new Date(), 1))

    const oldRanges: SelectValueListItem[] = [
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanThreeMonths'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -90)))}@${formatUtcIsoDateTime(tomorrow)}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanSixMonths'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -180)))}@${formatUtcIsoDateTime(tomorrow)}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanOneYear'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -365)))}@${formatUtcIsoDateTime(tomorrow)}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanTwoYears'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -730)))}@${formatUtcIsoDateTime(tomorrow)}`,
      },
    ]

    const newRanges: SelectValueListItem[] = [
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanOneHour'),
        value: `${formatUtcIsoDateTime(new Date(now.getTime() - 1 * 60 * 60 * 1000))}@${formatUtcIsoDateTime(now)}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.fromOneToTwoHours'),
        value: `${formatUtcIsoDateTime(new Date(now.getTime() - 2 * 60 * 60 * 1000))}@${formatUtcIsoDateTime(
          new Date(now.getTime() - 1 * 60 * 60 * 1000),
        )}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.fromTwoToThreeHours'),
        value: `${formatUtcIsoDateTime(new Date(now.getTime() - 3 * 60 * 60 * 1000))}@${formatUtcIsoDateTime(
          new Date(now.getTime() - 2 * 60 * 60 * 1000),
        )}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.fromThreeToFourHours'),
        value: `${formatUtcIsoDateTime(new Date(now.getTime() - 4 * 60 * 60 * 1000))}@${formatUtcIsoDateTime(
          new Date(now.getTime() - 3 * 60 * 60 * 1000),
        )}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanOneDay'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -1)))}@${formatUtcIsoDateTime(now)}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanTwoDays'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -2)))}@${formatUtcIsoDateTime(now)}`,
      },
    ]

    return [{ label: t('common.all'), value: '' }, ...oldRanges, ...newRanges]
  }, [addDays, t])
  const [financingProgramIds, setFinancingProgramIds] = useState<string[]>(filterValues.financingProgramIds)
  const [loanPurposeIds, setLoanPurposeIds] = useState<string[]>(filterValues.loanPurposeIds)
  const [team, setTeam] = useState<string>(filterValues.team)
  const [territoryIds, setTerritoryIds] = useState<string[]>(filterValues.territoryIds)
  const [progressionStatus, setProgressionStatus] = useState<string[]>(filterValues.progressionStatus)
  const [dateRange, setDateRange] = useState<string>(filterValues.dateRange)
  const [creditApplicationStatus, setCreditApplicationStatus] = useState<string>(filterValues.creditApplicationStatus)
  const [plan, setPlan] = useState<string[]>(filterValues.plan)
  const [merchantId, setMerchantId] = useState<string>(filterValues.merchantId)
  const [tag, setTag] = useState<string | null>(filterValues.tag || null)
  const [minAmountRequested, setMinAmountRequested] = useState<string>(filterValues.minAmountRequested || '')
  const [originSystemIds, setOriginSystemIds] = useState<string[]>(filterValues.originSystemIds || [])
  const MERCHANTS_LIMIT = 25
  const [debouncedQuery, setDebouncedQuery] = useState('')

  // Fetch merchant by ID for initial filter value
  const [fetchedMerchant, loadingMerchantById] = useGetMerchantById(merchantId)

  // Search merchants based on query
  const searchDto = useMemo(
    () =>
      debouncedQuery
        ? {
            financingCompanyId: selectedCompany,
            financingProgramId: null,
            nameContains: debouncedQuery,
            limit: MERCHANTS_LIMIT,
          }
        : null,
    [debouncedQuery, selectedCompany],
  )
  const [merchants, loadingMerchantSearch] = useSearchMerchants(searchDto)
  const loadingMerchant = loadingMerchantById || loadingMerchantSearch

  // Track selected merchant (from filter or user selection)
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null)

  // Update selected merchant when fetched merchant changes
  useEffect(() => {
    if (fetchedMerchant && !selectedMerchant) {
      setSelectedMerchant(fetchedMerchant)
    }
  }, [fetchedMerchant, selectedMerchant])

  const originSystemOptions: SelectValueListItem[] = useMemo(() => {
    return getEOriginSystemIdList().map((id) => ({
      label: t(`EOriginSystemId.${id}`, { ns: 'enum' }),
      value: id,
    }))
  }, [t])

  const filteredTerritories = useMemo(() => {
    const sourceSystem = selectedCompany === EFinancingCompany.Iceberg ? ESourceSystem.Monolith : ESourceSystem.Alis
    const filteredTerritoriesBySourceSystem = territories.filter((territory) => territory.sourceSystem === sourceSystem)
    return filteredTerritoriesBySourceSystem
  }, [territories, selectedCompany])

  const debouncedSetQuery = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedQuery(value)
      }, 1000),
    [],
  )

  const updateQuery = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    debouncedSetQuery(e.target.value)
  }

  const shouldQueryForAllCreditApplicationsStatus = (status: string[]) =>
    status.some((value) =>
      [
        EProgressionStatus.Quotation,
        EProgressionStatus.Submitted,
        EProgressionStatus.Booked,
        EProgressionStatus.WithDrawn,
      ].includes(value as EProgressionStatus),
    )

  const onChangeProgressionStatus = (status: string[]) => {
    if (shouldQueryForAllCreditApplicationsStatus(status)) setCreditApplicationStatus('')
    setProgressionStatus(status)
  }

  useEffect(() => {
    setFinancingProgramIds(filterValues.financingProgramIds)
    setTeam(filterValues.team)
    setTerritoryIds(filterValues.territoryIds)
    setLoanPurposeIds(filterValues.loanPurposeIds)
    setDateRange(filterValues.dateRange === '' ? dateRanges[0].value.toString() : filterValues.dateRange)
    setCreditApplicationStatus(
      filterValues.creditApplicationStatus ? filterValues.creditApplicationStatus : ECreditApplicationStatus.Active,
    )
    setPlan(filterValues.plan)
    setMerchantId(filterValues.merchantId)
    setTag(filterValues.tag || null)
    setMinAmountRequested(filterValues.minAmountRequested || '')
    setOriginSystemIds(filterValues.originSystemIds || [])
  }, [filterValues, dateRanges])

  const onApplyFilters = () => {
    applyFilter({
      ...filterValues,
      financingProgramIds,
      loanPurposeIds,
      team,
      dateRange,
      territoryIds,
      creditApplicationStatus,
      progressionStatus,
      plan,
      merchantId,
      tag,
      minAmountRequested,
      originSystemIds,
    })
    onCancel()
  }

  return (
    <Dialog
      onClose={onCancel}
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="lg"
    >
      <DialogTitle id="alert-dialog-title">{t('common.filter')}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Autocomplete
              id="merchantList"
              options={merchants}
              onChange={(_event, newMerchant: Merchant | null) => {
                if (newMerchant) {
                  setMerchantId(newMerchant.id)
                  setSelectedMerchant(newMerchant)
                }
              }}
              value={selectedMerchant}
              autoHighlight
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.name}
              noOptionsText={t('common.searchYieldsNoResults')}
              renderInput={(merchantList) => (
                <InputTextField
                  onChange={updateQuery}
                  {...merchantList}
                  label={t('common.merchant')}
                  slotProps={{
                    input: {
                      ...merchantList.InputProps,
                      endAdornment: loadingMerchant ? <CircularProgress color="inherit" size={20} /> : null,
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    color: option.isActive ? 'unset' : 'grey',
                    opacity: option.isActive ? 1 : 0.9,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start !important',
                    textAlign: 'left !important',
                  }}
                >
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.80rem', color: '#808080' }}>
                    {option.address}, {option.city}, {option.province}
                  </Typography>
                </Box>
              )}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <MultipleSelectCheckmarks
              title={t('credit.plan')}
              content={programPlans}
              defaultValues={plan}
              onValueChanged={(planValue: number[] | string[]) => {
                setPlan(planValue as string[])
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <MultipleSelectCheckmarks
              title={t('browseCreditApplications.dataGridHeader.territory')}
              content={filteredTerritories.map((item) => {
                return { label: item.name, value: item.id } as SelectValueListItem
              })}
              defaultValues={territoryIds}
              onValueChanged={(territoryId: number[] | string[]) => {
                setTerritoryIds(territoryId as string[])
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <MultipleSelectCheckmarks
              title={t('editCreditApplication.financingProgramType')}
              content={Object.values(filteredFinancingPrograms).map((financingProgram) => {
                const data: SelectValueListItem = {
                  label: financingProgram[lang],
                  value: financingProgram.id,
                }
                return data
              })}
              defaultValues={financingProgramIds}
              onValueChanged={(financingProgram: number[] | string[]) => {
                setFinancingProgramIds(financingProgram as string[])
              }}
            />
          </Grid>
          {selectedCompany === EFinancingCompany.IFinance && (
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <MultipleSelectCheckmarks
                title={t('editCreditApplication.loanPurpose')}
                content={getELoanPurposeList().map<SelectValueListItem>((item) => {
                  return { label: t(`ELoanPurpose.${item}`, { ns: 'enum' }), value: item }
                })}
                defaultValues={loanPurposeIds}
                onValueChanged={(loanPurposes: number[] | string[]) => {
                  setLoanPurposeIds(loanPurposes as string[])
                }}
              />
            </Grid>
          )}

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <SelectComponent
              items={dateRanges}
              label={t('common.created')}
              onChange={(e: { target: { value: SetStateAction<string> } }) => setDateRange(e.target.value)}
              value={dateRange}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <SelectComponent
              items={creditApplicationStatuses}
              label={t('browseCreditApplications.dataGridHeader.creditApplicationStatus')}
              onChange={(e: { target: { value: SetStateAction<string> } }) =>
                setCreditApplicationStatus(e.target.value)
              }
              value={creditApplicationStatus}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <MultipleSelectCheckmarks
              title={t('credit.progressionStatus')}
              content={getEProgressionStatusList().map((item) => ({
                label: t(`EProgressionStatus.${item}`, { ns: 'enum' }),
                value: item,
              }))}
              defaultValues={progressionStatus}
              onValueChanged={(status: number[] | string[]) => {
                onChangeProgressionStatus(status as string[])
              }}
            />
          </Grid>
          {selectedCompany === EFinancingCompany.IFinance && (
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <SelectComponent
                items={iFinanceTeams}
                label={t('common.team')}
                onChange={(e: { target: { value: SetStateAction<string> } }) => setTeam(e.target.value)}
                value={team}
              />
            </Grid>
          )}
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Autocomplete
              options={tagKeys}
              getOptionLabel={(opt) => tags.find((item) => item.id === opt)!.text}
              value={tag}
              onChange={(_e, value) => setTag(value)}
              renderInput={(params) => <InputTextField {...params} label={t('common.tags')} />}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <NumberField
              label={t('common.amountRequestedGreaterThan')}
              value={minAmountRequested}
              onChange={(e) => setMinAmountRequested(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <MultipleSelectCheckmarks
              title={t('common.origin')}
              content={originSystemOptions}
              defaultValues={originSystemIds}
              onValueChanged={(values: number[] | string[]) => {
                setOriginSystemIds(values as string[])
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonGroup>
          <Button
            variant="outlined"
            startIcon={<ClearIcon fontSize="small" color="error" />}
            onClick={() => {
              resetFilter()
              onCancel()
            }}
          >
            {t('taskManager.reset')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<KeyboardArrowRightIcon fontSize="small" color="success" />}
            onClick={onApplyFilters}
          >
            {t('taskManager.apply')}
          </Button>
        </ButtonGroup>
      </DialogActions>
    </Dialog>
  )
}
export default memo(SearchFilter)
